/*
 * Copyright (c) 2025. World Wide Generation Ltd
 */

import { useMemo } from 'react';
import type { GridItemContext, GridItemContextInput } from '@g17eco/types/dashboard';

/**
 * Build a GridItemContext from input props.
 * Converts arrays to maps and applies defaults for optional boolean props.
 *
 * @example
 * const gridItemContext = buildGridItemContext({
 *   initiativeId,
 *   readOnly: true,
 *   valueLists: dashboard.valueLists,
 *   surveyUnitConfigs: dashboard.surveyUnitConfigs,
 * });
 */
export const buildGridItemContext = (input: GridItemContextInput): GridItemContext => ({
  initiativeId: input.initiativeId,
  initiativeIds: input.initiativeIds,
  dataFilters: input.dataFilters,
  initiative: input.initiative,
  rootOrg: input.rootOrg,
  survey: input.survey,
  scorecard: input.scorecard,
  readOnly: input.readOnly ?? false,
  hideQuestionReference: input.hideQuestionReference ?? false,
  valueListMap: new Map((input.valueLists ?? []).map((list) => [String(list._id), list])),
  surveyUnitConfigMap: new Map((input.surveyUnitConfigs ?? []).map((config) => [String(config._id), config])),
});

/**
 * Hook to build and memoize GridItemContext.
 * Use this in components to avoid recreating the context on every render.
 *
 * The hook depends on individual dataFilters values (period, dateRange, timeRange, surveyType)
 * rather than the object reference, so callers can pass inline objects without breaking memoization.
 *
 * @example
 * const gridItemContext = useGridItemContext({
 *   initiativeId,
 *   dataFilters: { period, dateRange, timeRange }, // inline object is safe
 *   readOnly: true,
 *   valueLists: dashboard.valueLists,
 *   surveyUnitConfigs: dashboard.surveyUnitConfigs,
 * });
 */
export const useGridItemContext = (input: GridItemContextInput): GridItemContext => {
  const {
    initiativeId,
    initiativeIds,
    dataFilters,
    initiative,
    rootOrg,
    survey,
    scorecard,
    readOnly,
    hideQuestionReference,
    valueLists,
    surveyUnitConfigs,
  } = input;
  const hasDataFilters = dataFilters !== undefined;
  const { period, dateRange, timeRange, surveyType } = dataFilters ?? {};

  return useMemo(
    () =>
      buildGridItemContext({
        initiativeId,
        initiativeIds,
        dataFilters: hasDataFilters ? { period, dateRange, timeRange, surveyType } : undefined,
        initiative,
        rootOrg,
        survey,
        scorecard,
        readOnly,
        hideQuestionReference,
        valueLists,
        surveyUnitConfigs,
      }),
    [
      initiativeId,
      initiativeIds,
      period,
      dateRange,
      timeRange,
      surveyType,
      hasDataFilters,
      initiative,
      rootOrg,
      survey,
      scorecard,
      readOnly,
      hideQuestionReference,
      valueLists,
      surveyUnitConfigs,
    ],
  );
};
